import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';

const TIPS = [
  'Tip: Use a smaller brush for precise edges.',
  'Tip: The eraser tool (E) lets you refine your mask.',
  'Tip: Ctrl+Z undoes your last stroke.',
  'Tip: Hold Space and drag to pan around the image.',
  'Tip: Ctrl+scroll to zoom in for detail work.',
  'Tip: Be specific in your description for better results.',
  'Tip: You can iterate — edit the mask and regenerate.',
  'Tip: Scroll to adjust brush size without switching tools.',
  'Tip: Press B for brush, E for eraser — no mouse needed.',
];

const GENERATING = 'Generating';
const GENERATING_CHAR_MS = 60;
const TIP_CHAR_MS = 38;
const TIP_HOLD_MS = 6500;
const DOT_CYCLE_MS = 400;

const prefersReducedMotion =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

export function SkyrimLoading() {
  const overlayRef = useRef<HTMLDivElement>(null);

  // "Generating" types once on mount, then stays put while the "..."
  // progression takes over.
  const [generatingTyped, setGeneratingTyped] = useState(prefersReducedMotion ? GENERATING : '');
  const generatingDone = generatingTyped.length === GENERATING.length;

  useEffect(() => {
    if (generatingDone) return;
    const id = setTimeout(
      () => setGeneratingTyped(GENERATING.slice(0, generatingTyped.length + 1)),
      GENERATING_CHAR_MS,
    );
    return () => clearTimeout(id);
  }, [generatingTyped, generatingDone]);

  // "..." cycles 1 → 2 → 3 → 1 once "Generating" is fully typed.
  const [dotCount, setDotCount] = useState(1);
  useEffect(() => {
    if (!generatingDone || prefersReducedMotion) return;
    const id = setInterval(() => setDotCount((d) => (d % 3) + 1), DOT_CYCLE_MS);
    return () => clearInterval(id);
  }, [generatingDone]);

  // Tip rotation. Each new tip resets the typewriter; once a tip is fully
  // typed it holds for TIP_HOLD_MS before the next one starts re-typing.
  const [tipIndex, setTipIndex] = useState(0);
  const [tipTyped, setTipTyped] = useState(prefersReducedMotion ? (TIPS[0] ?? '') : '');
  const tipFull = TIPS[tipIndex] ?? '';
  const tipDone = tipTyped.length === tipFull.length;

  useEffect(() => {
    setTipTyped(prefersReducedMotion ? tipFull : '');
  }, [tipIndex, tipFull]);

  useEffect(() => {
    if (tipDone) return;
    const id = setTimeout(() => setTipTyped(tipFull.slice(0, tipTyped.length + 1)), TIP_CHAR_MS);
    return () => clearTimeout(id);
  }, [tipTyped, tipFull, tipDone]);

  useEffect(() => {
    if (!tipDone) return;
    const id = setTimeout(() => setTipIndex((i) => (i + 1) % TIPS.length), TIP_HOLD_MS);
    return () => clearTimeout(id);
  }, [tipDone]);

  // Auto-focus overlay to trap keyboard focus
  useEffect(() => {
    overlayRef.current?.focus();
  }, []);

  return (
    <motion.div
      ref={overlayRef}
      role="status"
      aria-modal="true"
      aria-label="Generation in progress"
      tabIndex={-1}
      initial={prefersReducedMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      {...(prefersReducedMotion ? {} : { exit: { opacity: 0 } })}
      transition={{ duration: 0.3 }}
      onKeyDown={(e) => {
        if (e.key === 'Tab') e.preventDefault();
      }}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-4)',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backgroundImage:
          'repeating-conic-gradient(rgba(255, 255, 255, 0.03) 0% 25%, transparent 0% 50%)',
        backgroundSize: '4px 4px',
        outline: 'none',
      }}
      data-testid="skyrim-loading"
    >
      <style>{`
        @keyframes skyrimBlink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        .skyrim-cursor-block {
          display: inline-block;
          width: 0.55em;
          height: 1em;
          background-color: var(--color-accent-cyan);
          vertical-align: -0.12em;
          margin-left: 0.18em;
          animation: skyrimBlink 0.9s steps(2) infinite;
        }
      `}</style>

      <h2
        style={{
          fontFamily: 'var(--font-sans)',
          color: 'var(--color-text-primary)',
          fontSize: 'var(--text-h2, 1.5rem)',
          fontWeight: 600,
          margin: 0,
          minHeight: '1.2em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {generatingTyped}
        {generatingDone && (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {'.'.repeat(dotCount)}
            <span style={{ opacity: 0 }}>{'.'.repeat(3 - dotCount)}</span>
          </span>
        )}
      </h2>

      <div
        aria-live="polite"
        style={{
          minHeight: '2em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: '640px',
          padding: '0 var(--space-4)',
        }}
      >
        <p
          style={{
            fontFamily: 'var(--font-mono)',
            color: 'var(--color-text-secondary)',
            // Bigger than the previous --text-body (~14px) — tip should
            // feel like the primary status reading material, not a hint.
            fontSize: '1rem',
            lineHeight: 1.5,
            margin: 0,
            textAlign: 'center',
          }}
        >
          {tipTyped}
          <span className="skyrim-cursor-block" aria-hidden="true" />
        </p>
      </div>
    </motion.div>
  );
}
